import React, {useState} from 'react';
import {Trans} from '@ui/i18n/trans';
import {useProducts} from '@common/billing/pricing-table/use-products';
import {Product} from '@common/billing/product';
import {Price} from '@common/billing/price';
import {
  DEFAULT_UPSELL_BILLING_CYCLE,
  findBestPrice,
  UpsellBillingCycle,
} from '@common/billing/pricing-table/find-best-price';
import {FormattedPrice} from '@common/billing/formatted-price';
import {ProductFeatureList} from '@common/billing/pricing-table/product-feature-list';
import {Button} from '@ui/buttons/button';
import {Chip} from '@ui/forms/input-field/chip-field/chip';
import clsx from 'clsx';
import {BillingCycleRadio} from '@common/billing/pricing-table/billing-cycle-radio';
import {UpsellLabel} from '@common/billing/pricing-table/upsell-label';
import {Skeleton} from '@ui/skeleton/skeleton';

interface Props {
  onSelect: (product: Product, price: Price | null) => void;
}

export function OnboardingPlanStep({onSelect}: Props) {
  const [cycle, setCycle] = useState<UpsellBillingCycle>(
    DEFAULT_UPSELL_BILLING_CYCLE,
  );
  const {data, isLoading} = useProducts();

  const hasPaidPlans = (data?.products ?? []).some(
    p => !p.free && !p.hidden && p.prices?.length > 0,
  );

  return (
    <div>
      <h1 className="mb-8 text-center text-2xl font-semibold">
        <Trans message="Choose your plan" />
      </h1>
      <p className="mb-24 text-center text-sm text-muted">
        <Trans message="Select the plan that fits you best. You can change it later." />
      </p>

      {hasPaidPlans && (
        <div className="mb-24 flex justify-center">
          <BillingCycleRadio
            selectedCycle={cycle}
            onChange={setCycle}
            products={data?.products}
          />
        </div>
      )}

      {isLoading ? (
        <PlanSkeletons />
      ) : (
        <div className="flex flex-col gap-16 md:flex-row md:justify-center">
          {(data?.products ?? [])
            .filter(p => !p.hidden)
            .map(product => {
              const price = findBestPrice(cycle, product.prices) ?? null;
              return (
                <PlanCard
                  key={product.id}
                  product={product}
                  price={price}
                  cycle={cycle}
                  products={data?.products ?? []}
                  onSelect={() => onSelect(product, price)}
                />
              );
            })}
        </div>
      )}
    </div>
  );
}

interface PlanCardProps {
  product: Product;
  price: Price | null;
  cycle: UpsellBillingCycle;
  products: Product[];
  onSelect: () => void;
}
function PlanCard({product, price, cycle, products, onSelect}: PlanCardProps) {
  return (
    <div
      className={clsx(
        'flex w-full flex-col rounded-panel border bg px-24 py-24 shadow-sm md:max-w-280',
        product.free && 'order-last md:order-none',
        product.recommended && 'border-primary',
      )}
    >
      <div className="mb-16 flex-1">
        <Chip
          radius="rounded"
          size="sm"
          className={clsx('mb-12 w-min', !product.recommended && 'invisible')}
        >
          <Trans message="Most popular" />
        </Chip>
        <div className="mb-6 text-lg font-semibold">
          <Trans message={product.name} />
        </div>
        <div className="mb-16 text-sm text-muted">
          <Trans message={product.description} />
        </div>
        <div className="mb-16">
          {price ? (
            <FormattedPrice
              priceClassName="font-bold text-3xl"
              periodClassName="text-muted text-xs"
              variant="separateLine"
              price={price}
            />
          ) : (
            <div className="text-3xl font-bold">
              <Trans message="Free" />
            </div>
          )}
          {price && <UpsellLabel products={products} cycle={cycle} />}
        </div>
        <ProductFeatureList product={product} />
      </div>

      <Button
        variant={product.recommended ? 'flat' : 'outline'}
        color="primary"
        className="mt-16 w-full"
        size="md"
        onClick={onSelect}
      >
        {product.free ? (
          <Trans message="Get started" />
        ) : (
          <Trans message="Select" />
        )}
      </Button>
    </div>
  );
}

function PlanSkeletons() {
  return (
    <div className="flex flex-col gap-16 md:flex-row md:justify-center">
      {[1, 2, 3].map(i => (
        <div
          key={i}
          className="w-full rounded-panel border px-24 py-90 md:max-w-280"
        >
          <Skeleton className="my-10" />
          <Skeleton className="mb-30" />
          <Skeleton className="mb-30 h-30" />
          <Skeleton className="mb-20 h-40" />
          <Skeleton />
          <Skeleton />
        </div>
      ))}
    </div>
  );
}
